import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { defaultLoanTypesOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

export type LoanFormValues = {
  value: string;
  startDate: string;
  type: Option<string> | null;
  description: string;
  endDate: string;
  interestRate: string;
};

export type LoanFormRef = {
  submit: () => void;
};

type LoanFormProps = {
  formRef?: Ref<LoanFormRef>;
  onSubmit?: (values: LoanFormValues) => void;
  defaultValues?: LoanFormValues;
};

const LoanForm: React.FC<LoanFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<LoanFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: LoanFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.type']}
        name="type"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultLoanTypesOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.loanAmount']}
        name="value"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item label={messages['common.interestRate']} name="interestRate">
        <AppPercentageInput
          placeholder={messages['form.enter']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.startDate']}
        name="startDate"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item label={messages['common.endDate']} name="endDate">
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default LoanForm;
